import { Group, Stack, Text, Title } from '@mantine/core';
import { createFileRoute } from '@tanstack/react-router';
import { useConvex } from 'convex/react';
import FloatingButton from '../../components/FloatingButton';

import { writingRoles } from '../../../convex/shared_util';
import { useDetailStack, useOpenDetailId } from '../../components/DetailStack';
import LayoutChooser, { useLayoutState } from '../../components/LayoutChooser';
import { openAddLocationModal } from '../../components/LocationDialog';
import { locationItemSelector } from '../../components/LocationItem';
import LocationList from '../../components/LocationList';
import useCurrentGarden from '../../hooks/useCurrentGarden';
import { useGardenLocations, useGardenPlants } from '../../hooks/useGardenData';
import { waitForElement } from '../../lib/util';

import classes from './panel.module.css';

export const Route = createFileRoute('/_app/g/$gardenToken/locations')({
  component: function LocationsPane() {
    const convex = useConvex();
    const { push: pushDetail } = useDetailStack();
    // the location `?v=` has open, which the table layout highlights
    const openLocationId = useOpenDetailId("location");
    const currentGarden = useCurrentGarden()!;
    const locations = useGardenLocations();
    const { activePlants } = useGardenPlants();

    const [layout, setLayout] = useLayoutState("locations-pane-2", "list", ["list", "table", "cards", "grid"]);

    return (
      <div className={classes.panel} data-scroll-restoration-id="locations-panel">

        {currentGarden && writingRoles.includes(currentGarden.role) && <FloatingButton
          label="Add location"
          onClick={() => openAddLocationModal(convex, currentGarden._id, (location) => {
            pushDetail({ location });
            waitForElement(locationItemSelector(location._id), (element) => {
              element.scrollIntoView({ behavior: "smooth", block: "center" });
            });
          })}
        />}

        <Stack gap="md">
          <Group mt="md" justify="space-between" align="center">
            <Title order={2}>Locations</Title>
            {locations && locations.length > 0 && (
              <LayoutChooser options={["table", "list", "cards", "grid"]} activeLayout={layout} setLayout={setLayout} />
            )}
          </Group>
          {locations && (
            <LocationList
              layout={layout}
              locations={locations}
              plants={activePlants || []}
              openId={openLocationId}
              onItemClick={(_, index) => pushDetail({ locations: locations.map((l) => ({ location: l })), activeIndex: index })}
            />
          )}
          {locations && locations.length <= 3 && (
            <Text c="dimmed" size="sm">
              Locations allow you to organize your plants by where they are in your garden. You can also tag journal entries by location.
            </Text>
          )}
        </Stack>
      </div>
    );
  },
})
